<script lang="ts">
  /**
   * One community on a leaderboard: its logo (or initial) with the `#rank` badge, and
   * a primary ring when it is the audited community itself.
   *
   * `light` is the variant that sits inside the dark hover strip, where a neutral badge
   * and ring would disappear into the card behind them. `size` 'sm' shrinks on a phone,
   * where ten logos have to fit a table cell; 'lg' holds its size.
   */
  import type { Leader } from '../../audit';

  let {
    leader,
    size = 'sm',
    light = false,
  }: { leader: Leader; size?: 'sm' | 'lg'; light?: boolean } = $props();

  const dim = $derived(size === 'lg' ? 'h-9 w-9' : 'h-7 w-7 sm:h-9 sm:w-9');
  const ring = $derived(leader.is_you ? 'ring-primary' : light ? 'ring-white' : 'ring-base-300');
  const badge = $derived(
    leader.is_you
      ? 'bg-primary text-primary-content'
      : light
        ? 'bg-white text-neutral-900'
        : 'bg-neutral text-neutral-content',
  );
  // The strip has no hover card of its own to name the community, so the logo does.
  const title = $derived(light ? `#${leader.rank} ${leader.name}` : undefined);
</script>

<span class="relative block shrink-0">
  {#if leader.logo_url}
    <!-- privacy-ok: public community logo, not a member -->
    <img
      src={leader.logo_url}
      alt={leader.name}
      {title}
      class="{dim} shrink-0 rounded-full object-cover ring-2 {ring}"
      loading={light ? undefined : 'lazy'}
    />
  {:else}
    <!-- privacy-ok: one initial of a public community name -->
    <span
      class="flex {dim} shrink-0 items-center justify-center rounded-full bg-base-300 text-xs
             font-semibold {light ? '' : `ring-2 ${ring}`}"
      {title}>{leader.name.slice(0, 1)}</span
    >
  {/if}
  <span
    class="absolute -left-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full
           px-1 text-[10px] font-bold leading-none tabular-nums {badge}"
  >#{leader.rank}</span>
</span>
